import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Grid,
  Snackbar,
  TextField,
} from "@mui/material";
import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import HttpService from "../services/HttpService";
import { useFormik } from "formik";
import * as yup from "yup";
import Labels from "../labels/Labels";

export default function AddEmployerAttribute(props) {
  const isEmployerScoped = Boolean(props.employerId);
  const createEmployeeAttributeMutation = useMutation(
    (values) => {
      values.companieList = values.companieList.map(
        (companie) => companie.idEmployer
      );
      return HttpService.getAxiosClient().post(
        window.API_URL + `/employer/attributes`,
        values
      );
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries("employerAttributes");
        props.handleClose();
      },
      onError: (error) => {
        setFeedback({
          open: true,
          type: "error",
          message: Labels.getLabel(
            "employerAttribute.createError",
            "Error creating the attributes, constraint violated."
          ),
        });
      },
    }
  );

  const validationSchema = yup.object({
    companieList: yup
      .array()
      .min(
        1,
        Labels.getLabel(
          "employerAttribute.companyRequired",
          "At least one company is required"
        )
      ),
    attribute: yup
      .string("Enter the attribute")
      .required(
        Labels.getLabel(
          "employerAttribute.attributeRequired",
          "Attribute is required"
        )
      ),
    value: yup
      .string("Enter the value")
      .required(
        Labels.getLabel("employerAttribute.valueRequired", "Value is required")
      ),
  });

  const formik = useFormik({
    initialValues: {
      companieList: [],
      attribute: "",
      value: "",
    },
    validationSchema: validationSchema,
    onSubmit: (values) => {
      createEmployeeAttributeMutation.mutate({ ...values });
    },
  });

  const queryClient = useQueryClient();

  async function fetchCompanyData() {
    const response = await HttpService.getAxiosClient().get(
      window.API_URL + `/employer/all`
    );
    return response;
  }

  async function fetchAttributesData() {
    const response = await HttpService.getAxiosClient().get(
      window.API_URL + `/employer/attributes/list`
    );
    return response;
  }

  const { data, isLoading } = useQuery(["companies"], () => fetchCompanyData());
  const [feedback, setFeedback] = useState({
    open: false,
    type: "error",
    message: "",
  });

  const { data: dataAttributes, isLoading: isLoadingAttributes } = useQuery(
    ["attributes"],
    () => fetchAttributesData()
  );

  React.useEffect(() => {
    if (!isEmployerScoped || !data?.data?.length) {
      return;
    }

    const matchingEmployer = data.data.find(
      (employer) => String(employer.idEmployer) === String(props.employerId)
    );

    const scopedEmployer = matchingEmployer || {
      idEmployer: props.employerId,
      description: props.employerName || String(props.employerId),
    };

    formik.setFieldValue("companieList", [scopedEmployer]);
  }, [data, isEmployerScoped, props.employerId]);

  return (
    <div>
      <Box
        component="form"
        noValidate
        autoComplete="off"
        onSubmit={formik.handleSubmit}
      >
        {isLoading || isLoadingAttributes ? (
          ""
        ) : (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Autocomplete
                fullWidth
                multiple
                id="companieList"
                options={data.data}
                disabled={isEmployerScoped}
                getOptionLabel={(option) =>
                  `${option.description}-[${option.idEmployer}]`
                }
                filterSelectedOptions
                value={formik.values.companieList}
                onChange={(event, selectedOptions) => {
                  formik.setFieldValue("companieList", selectedOptions);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    error={Boolean(formik.errors.companieList)}
                    label={Labels.getLabel(
                      "employerAttribute.companyList",
                      "Company list"
                    )}
                    helperText={
                      isEmployerScoped
                        ? `Company locked to ${props.employerName || props.employerId}`
                        : formik.errors.companieList
                    }
                    placeholder={Labels.getLabel(
                      "employerAttribute.company",
                      "Company"
                    )}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Autocomplete
                fullWidth
                freeSolo
                id="attribute"
                options={dataAttributes.data}
                value={formik.values.attribute}
                onChange={(event, newValue) => {
                  // Handle both selection from dropdown and typed input
                  formik.setFieldValue("attribute", newValue || "");
                }}
                onInputChange={(event, newInputValue) => {
                  // Handle typing when freeSolo is true
                  formik.setFieldValue("attribute", newInputValue);
                }}
                filterSelectedOptions
                renderInput={(params) => (
                  <TextField
                    {...params}
                    error={Boolean(formik.errors.attribute)}
                    helperText={formik.errors.attribute}
                    label={Labels.getLabel(
                      "employerAttribute.attribute",
                      "Attribute"
                    )}
                    placeholder={Labels.getLabel(
                      "employerAttribute.attribute",
                      "Attribute"
                    )}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                id="value"
                label={Labels.getLabel("employerAttribute.value", "Value")}
                value={formik.values.value}
                onChange={formik.handleChange}
                error={formik.touched.value && Boolean(formik.errors.value)}
                helperText={formik.touched.value && formik.errors.value}
              />
            </Grid>
            <Grid item xs={12} pt={3}>
              <Button
                fullWidth
                variant="contained"
                type="submit"
                disabled={createEmployeeAttributeMutation.isLoading}
              >
                {Labels.getLabel("employerAttribute.save", "SAVE")}
              </Button>
            </Grid>
          </Grid>
        )}
      </Box>
      <Snackbar
        open={feedback.open}
        autoHideDuration={6000}
        onClose={() => setFeedback({ ...feedback, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setFeedback({ ...feedback, open: false })}
          severity={feedback.type}
          sx={{ width: "100%" }}
        >
          {feedback.message}
        </Alert>
      </Snackbar>
    </div>
  );
}
